import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Backend-served media path ("/api/...") → absolute URL
export const mediaUrl = (u) => (u && u.startsWith("/") ? `${BASE}${u}` : u);

export const http = axios.create({ baseURL: API });

// Attach bearer token when present
http.interceptors.request.use((config) => {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("vornx-token") : null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Align dynamic content to the visitor's chosen language (site) — the app sends its own header.
  const lang = typeof window !== "undefined" ? window.localStorage.getItem("vornx-lang") : null;
  if (lang) config.headers["X-App-Language"] = lang;
  return config;
});

// Normalize FastAPI error details (string | array | object) → string
export function apiError(err, fallback = "Qualcosa è andato storto. Riprova.") {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

const LOCALE = { it: "it-IT", en: "en-IE", de: "de-DE", es: "es-ES", fr: "fr-FR" };

export function formatPrice(value, lang = "it") {
  return new Intl.NumberFormat(LOCALE[lang] || "it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value ?? 0);
}

// Store API surface
export const StoreAPI = {
  products: (params = {}) => http.get("/products", { params }).then((r) => r.data),
  product: (slug) => http.get(`/products/${slug}`).then((r) => r.data),
  createOrder: (payload) => http.post("/orders", payload).then((r) => r.data),
  payOrder: (id) => http.post(`/orders/${id}/pay`).then((r) => r.data),
  createCheckoutSession: (order_id, origin_url, preferred_method = null) => http.post("/store/checkout-session", { order_id, origin_url, preferred_method }).then((r) => r.data),
  paymentStatus: (session_id) => http.get(`/payments/status/${session_id}`).then((r) => r.data),
  order: (id, email) => http.get(`/orders/${id}`, { params: email ? { email } : {} }).then((r) => r.data),
  myOrders: () => http.get("/account/orders").then((r) => r.data),
  requestReturn: (order_id, reason = "", email) => http.post(`/orders/${order_id}/return-request`, { reason, email }).then((r) => r.data),
  withdraw: (order_id, payload) => http.post(`/orders/${order_id}/withdraw`, payload).then((r) => r.data),
  // auth
  login: (payload) => http.post("/auth/login", payload).then((r) => r.data),
  register: (payload) => http.post("/auth/register", payload).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  changePassword: (payload) => http.post("/auth/change-password", payload).then((r) => r.data),
  forgotPassword: (payload) => http.post("/auth/forgot-password", payload).then((r) => r.data),
  resetPassword: (payload) => http.post("/auth/reset-password", payload).then((r) => r.data),
  deleteAccount: () => http.delete("/auth/account").then((r) => r.data),
  // admin
  adminStats: () => http.get("/admin/stats").then((r) => r.data),
  adminCockpit: () => http.get("/admin/cockpit").then((r) => r.data),
  adminActionCenter: () => http.get("/admin/action-center").then((r) => r.data),
  adminSetActionStatus: (payload) => http.post("/admin/action-center/status", payload).then((r) => r.data),
  adminLeads: () => http.get("/admin/leads").then((r) => r.data),
  adminUpdateLeadStatus: (id, status) => http.put(`/admin/leads/${id}/status`, { status }).then((r) => r.data),
  adminMarketing: () => http.get("/admin/marketing").then((r) => r.data),
  adminLiveVisitors: () => http.get("/admin/live-visitors").then((r) => r.data),
  adminPurgeTestData: () => http.post("/admin/maintenance/purge-test-data").then((r) => r.data),
  // Content Studio + Editorial Calendar
  studioGenerateContent: (p) => http.post("/admin/studio/content/generate", p).then((r) => r.data),
  studioContent: () => http.get("/admin/studio/content").then((r) => r.data),
  studioCreateContent: (p) => http.post("/admin/studio/content", p).then((r) => r.data),
  studioUpdateContent: (id, p) => http.put(`/admin/studio/content/${id}`, p).then((r) => r.data),
  studioDeleteContent: (id) => http.delete(`/admin/studio/content/${id}`).then((r) => r.data),
  studioCalendar: () => http.get("/admin/studio/calendar").then((r) => r.data),
  studioImages: () => http.get("/admin/studio/images").then((r) => r.data),
  studioGenerateImage: (p) => http.post("/admin/studio/images/generate", p).then((r) => r.data),
  studioDeleteImage: (id) => http.delete(`/admin/studio/images/${id}`).then((r) => r.data),
  studioUploadImage: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return http.post("/admin/studio/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  fileUrl: (path) => `${API}/admin/studio/files/${path}?auth=${typeof window !== "undefined" ? window.localStorage.getItem("vornx-token") : ""}`,
  adminProducts: () => http.get("/admin/products").then((r) => r.data),
  adminCreateProduct: (p) => http.post("/admin/products", p).then((r) => r.data),
  adminUpdateProduct: (id, p) => http.put(`/admin/products/${id}`, p).then((r) => r.data),
  adminRestockProduct: (id, quantity, batch_label) => http.post(`/admin/products/${id}/restock`, { quantity, batch_label }).then((r) => r.data),
  adminDiscoveryCredits: () => http.get("/admin/discovery-credits").then((r) => r.data),
  adminDeleteProduct: (id) => http.delete(`/admin/products/${id}`).then((r) => r.data),
  elogySyncProducts: () => http.post("/admin/elogy/sync-products").then((r) => r.data),
  importStarterCatalog: () => http.post("/admin/catalog/import-starter").then((r) => r.data),
  refreshCatalogImages: () => http.post("/admin/catalog/refresh-images").then((r) => r.data),
  adminOrders: () => http.get("/admin/orders").then((r) => r.data),
  adminUsers: () => http.get("/admin/users").then((r) => r.data),
  adminUserDetail: (email) => http.get("/admin/users/detail", { params: { email } }).then((r) => r.data),
  adminUpdateOrderStatus: (id, payload) => http.put(`/admin/orders/${id}/status`, payload).then((r) => r.data),
  adminIntegrations: () => http.get("/admin/integrations").then((r) => r.data),
  adminRetryIntegration: (payload) => http.post("/admin/integrations/retry", payload).then((r) => r.data),
  adminSimulateFailure: (payload) => http.post("/admin/integrations/simulate-failure", payload).then((r) => r.data),
  adminMarkNotificationRead: (id) => http.post(`/admin/notifications/${id}/read`).then((r) => r.data),
  // Fatture in Cloud
  ficStatus: () => http.get("/integrations/fattureincloud/status").then((r) => r.data),
  ficStart: () => http.get("/integrations/fattureincloud/start").then((r) => r.data),
  ficDisconnect: () => http.post("/integrations/fattureincloud/disconnect").then((r) => r.data),
  ficDocuments: () => http.get("/admin/fic/documents").then((r) => r.data),
  ficDiagnostics: () => http.get("/admin/fic/diagnostics").then((r) => r.data),
  ficApprove: (orderId) => http.post(`/admin/fic/approve/${orderId}`).then((r) => r.data),
  ficRefresh: (orderId) => http.post(`/admin/fic/refresh/${orderId}`).then((r) => r.data),
  // Reviews (native)
  productReviews: (slug) => http.get(`/products/${slug}/reviews`).then((r) => r.data),
  reviewInvitation: (token) => http.get(`/reviews/invitation/${token}`).then((r) => r.data),
  submitReview: (payload) => http.post("/reviews", payload).then((r) => r.data),
  uploadReviewPhoto: (token, file) => {
    const fd = new FormData();
    fd.append("token", token);
    fd.append("file", file);
    return http.post("/reviews/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  adminReviews: (status) => http.get("/admin/reviews", { params: status ? { status } : {} }).then((r) => r.data),
  adminSetReviewStatus: (id, status) => http.put(`/admin/reviews/${id}/status`, { status }).then((r) => r.data),
  adminDeleteReview: (id) => http.delete(`/admin/reviews/${id}`).then((r) => r.data),
};

// VORNX Club API surface
export const ClubAPI = {
  me: () => http.get("/club/me").then((r) => r.data),
  updateProfile: (name) => http.put("/club/profile", { name }).then((r) => r.data),
  communications: () => http.get("/club/communications").then((r) => r.data),
  magazine: () => http.get("/club/magazine").then((r) => r.data),
  article: (slug) => http.get(`/club/magazine/${slug}`).then((r) => r.data),
  joinWaitlist: (note = "") => http.post("/club/waitlist", { note }).then((r) => r.data),
  // admin — founders
  adminFounders: () => http.get("/admin/club/founders").then((r) => r.data),
  adminAssignFounder: (p) => http.post("/admin/club/founders", p).then((r) => r.data),
  adminInviteFounder: (p) => http.post("/admin/club/founders/invite", p).then((r) => r.data),
  adminConfirmFounder: (id) => http.post(`/admin/club/founders/${id}/confirm`).then((r) => r.data),
  adminUpdateFounder: (id, p) => http.put(`/admin/club/founders/${id}`, p).then((r) => r.data),
  adminRevokeFounder: (id) => http.delete(`/admin/club/founders/${id}`).then((r) => r.data),
  adminWaitlist: () => http.get("/admin/club/waitlist").then((r) => r.data),
  // admin — communications
  adminComms: () => http.get("/admin/club/communications").then((r) => r.data),
  adminCreateComm: (p) => http.post("/admin/club/communications", p).then((r) => r.data),
  adminUpdateComm: (id, p) => http.put(`/admin/club/communications/${id}`, p).then((r) => r.data),
  adminDeleteComm: (id) => http.delete(`/admin/club/communications/${id}`).then((r) => r.data),
  // admin — magazine
  adminMagazine: () => http.get("/admin/club/magazine").then((r) => r.data),
  adminCreateArticle: (p) => http.post("/admin/club/magazine", p).then((r) => r.data),
  adminUpdateArticle: (id, p) => http.put(`/admin/club/magazine/${id}`, p).then((r) => r.data),
  adminDeleteArticle: (id) => http.delete(`/admin/club/magazine/${id}`).then((r) => r.data),
  // admin — Founder Points
  adminPointsFounders: () => http.get("/admin/club/points/founders").then((r) => r.data),
  adminPointsLedger: (userId) => http.get(`/admin/club/points/ledger`, { params: { user_id: userId } }).then((r) => r.data),
  adminPointsAdjust: (p) => http.post("/admin/club/points/adjust", p).then((r) => r.data),
  adminPointsConfig: () => http.get("/admin/club/points/config").then((r) => r.data),
  adminUpdatePointsConfig: (p) => http.put("/admin/club/points/config", p).then((r) => r.data),
  adminPointsOrderCheck: (orderId) => http.get(`/admin/club/points/order/${orderId}`).then((r) => r.data),
  adminPointsTimeseries: () => http.get("/admin/club/points/timeseries").then((r) => r.data),
  adminPointsExportCsv: () => http.get("/admin/club/points/export", { responseType: "blob" }).then((r) => r.data),
};
